import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Self,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';

import { Subject } from 'rxjs';

import { CynoFieldControl } from '../core/cyno-field-control';
import { ErrorStateMatcher } from '../core/error-options';
import { CanUpdateErrorState, mixinErrorState } from '../core/error-state';
import { NgbCalendar, NgbDate, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { DateService, DateStruct } from 'outshared-lib';

class CynoDatepickerBase {
    constructor(
        public _defaultErrorStateMatcher: ErrorStateMatcher,
        public _parentForm: NgForm,
        public _parentFormGroup: FormGroupDirective,
        public ngControl: NgControl
    ) {
    }
}

const _CynoDatepickerMixinBase = mixinErrorState(CynoDatepickerBase);

class CynoDatepicker {
    constructor() {
    }
}

@Component({
    selector: 'cyno-datepicker',
    templateUrl: './cyno-datepicker.component.html',
    styleUrls: ['./cyno-datepicker.component.scss'],
    providers: [{provide: CynoFieldControl, useExisting: CynoDatepickerInputComponent}],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CynoDatepickerInputComponent extends _CynoDatepickerMixinBase
    implements ControlValueAccessor, CynoFieldControl<CynoDatepicker>, OnInit, DoCheck, OnDestroy, CanUpdateErrorState {

    @Input() get id() {
        return this._id ? this._id : `cyno-datepicker-${CynoDatepickerInputComponent.nextId}`;
    }

    set id(id) {
        this._id = id;
        this.stateChanges.next();
    }

    @Input() get required(): boolean {
        return this._required;
    }

    set required(req) {
        this._required = req;
        this.stateChanges.next();
    }

    @Input() get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value: boolean) {
        this._disabled = value;
        this.stateChanges.next();
    }

    @Input() set selectPastDates(isPast: boolean) {
        if (isPast) {
            const yesterday = this.dateService.subtract(1, 'day');
            const date = this.dateService.toNgbDateStruct(yesterday);
            this.maxDate = new NgbDate(date.year, date.month, date.day);
            this.minDate = new NgbDate(0, 0, 0);
        }

        this.stateChanges.next();
    }

    @Input() set selectPastDatesWithToday(isPast: boolean) {
        if (isPast) {
            this.maxDate = new NgbDate(this.dateService.year(), this.dateService.month(), this.dateService.day());
            this.minDate = new NgbDate(0, 0, 0);
        }

        this.stateChanges.next();
    }

    @Input() get minimumDate(): DateStruct {
        return this.minDate;
    }

    set minimumDate(minDate: DateStruct) {
        this.minDate = new NgbDate(minDate.year, minDate.month, minDate.day);
    }
    protected _disabled: boolean = false;

    static nextId = 0;
    private _id: string;
    private _required = false;
    @ViewChild('d', {static: true}) d: NgbInputDatepicker;
    @ViewChild('datepicker', {static: true}) datepicker: ElementRef;

    public value: string;
    public focused: boolean = false;
    public placeholder: string;
    public label: string;
    public minDate: NgbDate = new NgbDate(0, 0, 0);
    public maxDate: NgbDate = new NgbDate(0, 0, 0);

    public stateChanges: Subject<void> = new Subject();
    public errorState: boolean = null;
    public canOpen: boolean = true;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        public _defaultErrorStateMatcher: ErrorStateMatcher,
        private calendar: NgbCalendar,
        private _elementRef: ElementRef,
        private readonly dateService: DateService,
        @Optional() _parentForm: NgForm,
        @Optional() _parentFormGroup: FormGroupDirective,
        @Optional() @Self() public ngControl: NgControl
    ) {
        super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl);

        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        const now = this.dateService.toNgbDateStruct(undefined);
        this.minimumDate = new NgbDate(now.year, now.month, now.day);
    }

    @HostListener('document:click', ['$event.target']) onDocumentClick(event): void {
        if (this.d.isOpen() && !this._elementRef.nativeElement.contains(event)) {
            this.close();
        }
    }

    public onContainerClick(event: MouseEvent): void {}

    ngOnInit(): void {
        this.stateChanges.next();
    }

    public ngDoCheck(): void {
        if (this.ngControl) {
            this.updateErrorState();
        }
    }

    public writeValue(value: string): void {
        if (value) {
            this.datepicker.nativeElement.value = this.reformatDate(value);
        }
    }

    public registerOnChange(fn: (value: any) => void): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: () => {}): void {
        this._onTouched = fn;
    }

    public isToday(date: NgbDate): boolean {
        const toCheck = this.toNgbDateStruct(date);
        return this.dateService.isSame(toCheck);
    }

    public isTomorrow(date: NgbDate): boolean {
        const toCheck = this.toNgbDateStruct(date);
        const same = this.dateService.add(1, 'day');
        return this.dateService.isSame(toCheck, same);
    }

    public isPast(date: NgbDate): boolean {
        const toCheck = this.toNgbDateStruct(date);
        const before = this.toNgbDateStruct(this.minDate);

        return this.dateService.isBefore(toCheck, before);
    }

    public isWeekend(date: NgbDate): boolean {
        return this.calendar.getWeekday(date) >= 6;
    }

    public onSelect($event: NgbDate): void {
        const ngbDateStruct = this.toNgbDateStruct($event);
        const date = this.dateService.add(12, 'hour', ngbDateStruct);

        if (this.dateService.isValid(date)) {
            this._onChange(date);
            this._onTouched();
            this.errorState = false;
        } else {
            this._onChange(null);
            this.errorState = true;
        }
        this.close();
    }

    public onBlur(): void {
        if (this.datepicker.nativeElement.value) {
            this._onTouched();
        } else {
            this._onChange(null);
            if (!this.d.isOpen()) {
                this.errorState = true;
            }
        }
    }

    public toggle(): void {
        if (!this.d.isOpen() && this.canOpen) {
            this.open();
        } else if (this.d.isOpen() && !this.canOpen) {
            this.close();
        }
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
    }

    private _onChange: (value: any) => void = () => {};
    private _onTouched = () => {};

    private close(): void {
        this.canOpen = false;
        this.d.close();
        setTimeout(() => {
            this.canOpen = true;
        }, 300);
    }

    private open(): void {
        setTimeout(() => {
            this.canOpen = false;
        }, 300);
        this.d.open();
    }

    private toNgbDateStruct(date: NgbDate): DateStruct {
        return {year: date.year, month: date.month, day: date.day};
    }

    private reformatDate(value: string): string {
        return value.split('-').reverse().join('-');
    }
}
